import { flexRender, getCoreRowModel, type RowData, useReactTable } from '@tanstack/react-table';
import { memo } from 'react';
import AddRowButton from '@/components/table/add-row-button.component';
import { columns } from '@/components/table/columns';
import EmptyTable from '@/components/table/empty-table.component';
import { useAppSelector } from '@/redux/hooks';
import { selectData } from '@/redux/slices/table/selectors';
import { cn } from '@/shared/utils';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    type?: string;
    placeholder?: string;
    min?: number;
    max?: number;
    width?: number | string;
    minWidth?: number | string;
    maxWidth?: number | string;
  }
  /* eslint-enable @typescript-eslint/no-unused-vars */
}

const Table = () => {
  const data = useAppSelector(selectData);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  const rows = table.getRowModel().rows;

  return (
    <div className="relative h-full w-full">
      <table className="min-w-full select-none">
        {/* Table Header */}
        <thead className="sticky bg-gray-5 text-gray-1">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="p-3 text-left text-xs font-medium uppercase">
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {/* Table Body */}
        <tbody className="bg-mono-1">
          {rows.map((row) => {
            return (
              <tr key={row.id} className="odd:bg-mono-1 even:bg-gray-5">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn('px-2 py-6 text-sm whitespace-normal text-gray-1', {
                      'align-top': cell.column.id !== 'actions',
                    })}
                    style={{
                      width: cell.column.columnDef.meta?.width ?? 'auto',
                      maxWidth: cell.column.columnDef.meta?.maxWidth ?? 'auto',
                      minWidth: cell.column.columnDef.meta?.minWidth ?? 'auto',
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={columns.length}>
              <AddRowButton label="Add New" rowCount={rows.length} />
            </td>
          </tr>
        </tfoot>
      </table>
      {rows.length === 0 && <EmptyTable />}
    </div>
  );
};

export default memo(Table);
